/**
 * This file imports the third party library dependencies, then creates the angular module "demo"
 * and exports it.
 */

// External dependencies
import * as angular from 'angular';
import uiRouter from '@uirouter/angularjs';
import { upgradeModule } from '@uirouter/angular-hybrid';

// // Feature Modules
import { globalModule } from './global/index';
import { homeModule } from './home/index';
import { mymessagesModule } from './mymessages/index';
import '../app-ds3'; // <-- this is a barrel to import all the ds3 scripts in the right order
import { ThemeService } from './shared/services/theme.service';
import { downgradeInjectable } from '@angular/upgrade/static';
import { FeaturesService } from './shared/services/features.service';
import { CurrentUserService } from './shared/services/current-user.service';
import { FileDownloadService } from './shared/services/file-download.service';


// Create the angular 1 module "app-ds3".
//
// Since it is exported, other parts of the application (in other files) can then import it and register things.
// In bootstrap.js, the module is imported, and the components, services, and states are registered.
export const EICDataStream3Module = angular.module('EICDataStream3.module',
  [
    uiRouter,
    upgradeModule.name,
    homeModule.name,
    globalModule.name,
    mymessagesModule.name,

    "enums",
    "ngResource",
    "ui.router",
    "ui.bootstrap",

    "EICDataStream3.HttpInterceptors",
    "EICDataStream3.config",
    "EICDataStream3.dashboard",
    'EICDataStream3.projectDataServices',
    "EICDataStream3.search",
    'EICDataStream3.graphicalAnalysis',
    'EICDataStream3.notificationCentre',
    'EICDataStream3.projectNotifications',
    "EICDataStream3.pdfReportService",
    "EICDataStream3.exports",
    "EICDataStream3.queryBuilder",
    "EICDataStream3.projectDisplayModule",
    "EICDataStream3.businessDevelopmentTools",
    "EICDataStream3.userAdmin",
    "EICDataStream3.GeographicalSummary",

    // 'angulartics',
    // 'angulartics.google.analytics',
    'nvd3',
    'ngStorage',
    'SummaryDownload',
    'PasswordExpiryWarning',
    'ngMaterial',
    'ngAria',
    'dialogService.module',
    'lookupTables.module',
    'messageOfTheDay.module'
  ])
  .config(['$mdThemingProvider',
    function ($mdThemingProvider) {
      // EIC Theme for AngularJS Material
      $mdThemingProvider
        .definePalette('eicBluePalette', {
          50: '#e1f5ff',
          100: '#b2e5ff',
          200: '#7ed3ff',
          300: '#47c2ff',
          400: '#14b5ff',
          500: '#00a7fe',
          600: '#0099ef',
          700: '#0086da',
          800: '#0075c6',
          900: '#0055a4',
          A100: '#b2e5ff',
          A200: '#7ed3ff',
          A400: '#14b5ff',
          A700: '#0086da',
          // By default, text (contrast) on this palette should be white with 87% opacity.
          'contrastDefaultColor': 'light',
          // By default, for these lighter hues, text (contrast) should be 'dark'.
          'contrastDarkColors': '50 100 200 300 400 500 600 A100 A200 A400',
          // By default, for these darker hues, text (contrast) should be white with 100% opacity.
          'contrastStrongLightColors': '700 800 900 A700'
        })
        .definePalette('eicOrangePalette', {
          50: '#fffde8',
          100: '#fffac6',
          200: '#fef59f',
          300: '#fdf179',
          400: '#fbec5a',
          500: '#f9e73d',
          600: '#fedb3f',
          700: '#fcc336',
          800: '#faac2e',
          900: '#f58320',
          A200: '#fef59f',
          A100: '#ffe95f',
          A400: '#fbec5a',
          A700: '#fcc336',


          // By default, text (contrast) on this palette should be white with 87% opacity.
          'contrastDefaultColor': 'light',
          // By default, for these lighter hues, text (contrast) should be 'dark'.
          'contrastDarkColors': '50 100 200 300 400 500 600 A100 A200 A400',
          // By default, for these darker hues, text (contrast) should be white with 100% opacity.
          'contrastStrongLightColors': '700 800 900 '
        });

      $mdThemingProvider.theme('default')
        .primaryPalette('eicBluePalette', {
          'default': '900',
          'hue-1': '100', // use shade 100 for the <code>md-hue-1</code> class
          'hue-2': '600', // use shade 600 for the <code>md-hue-2</code> class
          'hue-3': 'A100' // use shade A100 for the <code>md-hue-3</code> class
        })
        .accentPalette('eicOrangePalette', {
          'default': '900',
          'hue-1': '100', // use shade 100 for the <code>md-hue-1</code> class
          'hue-2': '600', // use shade 600 for the <code>md-hue-2</code> class
          'hue-3': 'A100' // use shade A100 for the <code>md-hue-3</code> class
        });

      // $mdThemingProvider.theme('default')
      //   .primaryPalette('pink', {
      //     'default': '400', // by default use shade 400 from the pink palette for primary intentions
      //     'hue-1': '100', // use shade 100 for the <code>md-hue-1</code> class
      //     'hue-2': '600', // use shade 600 for the <code>md-hue-2</code> class
      //     'hue-3': 'A100' // use shade A100 for the <code>md-hue-3</code> class
      //   });
      // If you specify less than all of the keys, it will inherit from the
      // default shades
      // .accentPalette('purple', {
      //   'default': '200' // use shade 200 for default, and keep all other shades the same
      // });
    }
  ])
  .config([
    '$ariaProvider',
    function ($ariaProvider) {

      // Stop the aria provider from going mad and putting tabIndex everywhere
      $ariaProvider.config({
        tabindex: false
      });
    }
  ])
  .config([
    '$httpProvider',
    function ($httpProvider) {
      // set up an http interceptor. Principally, this is for ensuring proper
      // route transition if an api call fails owing to not being logged in

      // THis interceptor is obsolete because we are no longer handling tokens in the browswer
      // Instead, the BFF pattern places all security concerns n the backend
      // $httpProvider.interceptors.push('oidcHttpInterceptor');
      // $httpProvider.interceptors.push('unauthenticatedRedirectionInteceptor');

      $httpProvider.interceptors.push('versionCacheBustInterceptor');
      $httpProvider.interceptors.push('bffInterceptor');
      $httpProvider.interceptors.push('apiToBffInterceptor');

      $httpProvider.interceptors.push('httpMethodOverrideInteceptor');
      //$httpProvider.interceptors.push('httpTemplateFingerprintInterceptor');
      //  $httpProvider.interceptors.push('httpProjectCountThrottleInteceptor');
    }
  ])
  .config([
    '$logProvider', '$compileProvider', '$locationProvider', 'APP_META',
    function ($logProvider,
      $compileProvider,
      $locationProvider,
      APP_META) {
      $locationProvider.html5Mode(true); //.hashPrefix('');
      $compileProvider.debugInfoEnabled(APP_META.diagnostics.debugInfoEnabled);
      $logProvider.debugEnabled(true);
    }
  ])
  .config([
    "$provide", function ($provide) {

      $provide.decorator("$exceptionHandler",
        [
          "$delegate", "$injector", function ($delegate, $injector) {
            return function (exception, cause) {
              var $rootScope = $injector.get("$rootScope");

              if (cause == "<oidc-authorize>") {
                // broadcast the error for the authorise cmponent to act upon
                // $rootScope.addError({ message: "Exception", reason: exception });
                $rootScope.$broadcast('OIDC_AUTHORIZE_EXCEPTION', { exception: exception, cause: cause });
              }

              $delegate(exception, cause);
            };
          }
        ]);

    }
  ])
  .constant('versionFingerprint', "v-20251025_1")
  .factory('themeService', downgradeInjectable(ThemeService))
  .factory('featuresService', downgradeInjectable(FeaturesService))
  .factory('fileDownloadService', downgradeInjectable(FileDownloadService))
  //  .factory('CurrentUser', downgradeInjectable(CurrentUserService))

  // Request interceptor to override PATCH requests and tune
  // them into POSTs, setting the X-HTTP-Method-Override header
  // This is a workaround because some web proxies (eg WatchGuard)
  // block the PATCH verb by default
  .factory('httpMethodOverrideInteceptor',
    [
      '$q', function ($q) {
        var httpMethodOverrideInteceptor = {
          request: function (request) {
            // if the method is a PATCH, change it to  a POST and
            // add the X-HTTP-Method-Override header
            if (request.method === "PATCH") {
              request.method = "POST";
              request.headers["X-HTTP-Method-Override"] = "PATCH";
            }

            return request;
          }

        };

        return httpMethodOverrideInteceptor;
      }
    ])
  // Request interceptor to add version key to template 
  // files (ie those ending in .html)
  // A URL rewrite at the server end reverses this operation,
  // resulting in a file-reload when the version changes
  .factory('httpTemplateFingerprintInterceptor',
    [
      "versionFingerprint", function (versionFingerprint) {
        var httpTemplateFingerprintInterceptor = {
          request: function (request) {

            // versioning on the URL then using URLRewrite to remove the version number
            //This would be a great method but it
            //won;t work!

            // ignore links that start with 'uib/template/', they belong to ui.bootstrap
            if (request.url.startsWith("uib/template/")) return request;
            if (request.url.startsWith("templates/")) return request;

            // if the request URL ends in .html, add a fingerprint
            if (request.url.indexOf(".html") !== -1) { // this is an html file
              // this is an html file
              ////var position = request.url.lastIndexOf("/");
              ////if (position !== -1) {
              ////  // inject the version number
              ////  var fingerprintedUrl = [
              ////    request.url.slice(0, position),
              ////    versionFingerprint,
              ////    request.url.slice(position)
              ////  ].join('');

              ////  request.url = fingerprintedUrl;
              ////}

              // Append a querystring to the file with the fingerprint value
              var fingerprintedUrl = request.url + '?v=' + versionFingerprint;

              request.url = fingerprintedUrl;
            }

            return request;
          }

        };

        return httpTemplateFingerprintInterceptor;
      }
    ])
  .controller('MyController',
    [
      '$scope', '$timeout', '$mdSidenav', '$mdMedia', 'featuresService', function ($scope, $timeout, $mdSidenav, $mdMedia, featuresService) {
        var self = this;

        $scope.product = featuresService.product;
        document.title = $scope.product.fullName;

        $scope.openMenu = openMenu;
        $scope.closeMenu = closeMenu;

        $scope.$on("showOverlay", onOverlayChange);

        // ensure that the menu behaves in a predictable way. When 
        // the screen is expanded on a desktop, the dashboard should 
        // be locked open *and* put into the opened state so  that 
        // narrowing the window causes the menu to retract. It looks
        // odd if the menu stays open because it was opened before the 
        // screen window was widened.
        $scope.$watch(function () {
          return $mdMedia('gt-sm');
        },
          function () {
            var leftSideNav = $mdSidenav("left")

            if (leftSideNav.isLockedOpen()) {
              leftSideNav.close();
            }
          });

        // *********************
        // Internal methods
        // *********************
        function closeMenu() {
          $timeout(function () {
            $mdSidenav('left').close();
          });
        }

        function openMenu() {
          $timeout(function () {
            $mdSidenav('left').open();
          });
        }

        function onOverlayChange(newState: boolean) {
          return newState;
        }

      }
    ]);



// Apply some global configuration...

// If the user enters a URL that doesn't match any known URL (state), send them to `/welcome`
const otherwiseConfigBlock = ['$urlRouterProvider', '$locationProvider', ($urlRouterProvider, $locationProvider) => {
  $locationProvider.hashPrefix('');


  //$urlRouterProvider.otherwise('/welcome');
  // if a project url has a trailing slash, it needs to be removed
  // to maintain backwards compatibility with EICDataStream -  which 
  // required the trailing /
  $urlRouterProvider.rule(function ($injector, $location) {
    var path = $location.path(), search = $location.search();
    if (path.includes('/search/project/') && path[path.length - 1] === '/') {
      path = path.slice(0, -1);
      return path;
    }

  });

  // If a nameless Project Summary URL does not have a trailing slash, add one.
  // The name parameter is optional, but the router will choke if the trailing 
  // slash is left out
  $urlRouterProvider.rule(function ($injector, $location) {
    // get the path and look for a guid
    var path = $location.path().toLowerCase();
    if (!path.includes('/search/contractawardsummary/'))
      return //path;

    // look for a guid and return if one is not found
    var guidEndIndex = findGuidEndIndex(path);
    if (guidEndIndex === -1) return //path

    // if the guid id the last thing in the url, 
    // as a trailing slash
    if (path.length === guidEndIndex) {
      path = path + "/";
      return path;
    }

  });

  // If a nameless Contract Award Summary URL does not have a trailing slash,
  // add one. The name parameter is optional, but the router will choke if 
  // the trailing slash is left out
  $urlRouterProvider.rule(function ($injector, $location) {
    // get the path and look for a guid
    var path = $location.path().toLowerCase();
    if (!path.includes('/search/projectsummary/'))
      return //path;

    // look for a guid and return if one is not found
    var guidEndIndex = findGuidEndIndex(path);
    if (guidEndIndex === -1) return //path

    // if the guid id the last thing in the url, 
    // as a trailing slash
    if (path.length === guidEndIndex) {
      path = path + "/";
      return path;
    }

  });

  var findGuidEndIndex = function (str) {
    const regex = /[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}/i;
    const match = str.match(regex);
    return match ? match.index + match[0].length : -1;
  }


}];
EICDataStream3Module.config(otherwiseConfigBlock);

// Enable tracing of each TRANSITION... (check the javascript console)

// This syntax `$trace.enable(1)` is an alternative to `$trace.enable("TRANSITION")`.
// Besides "TRANSITION", you can also enable tracing for : "RESOLVE", "HOOK", "INVOKE", "UIVIEW", "VIEWCONFIG"
const traceRunBlock = ['$trace', $trace => { $trace.enable("TRANSITION", "RESOLVE", "HOOK", "INVOKE", "UIVIEW", "VIEWCONFIG"); }];
EICDataStream3Module.run(traceRunBlock);
